import { useMemo, useState } from 'react';
import { ChevronDown, Copy, RotateCcw, Sparkles } from 'lucide-react';
import {
  DECOR_LIBRARY_CATEGORY_SPECS,
  categoriesForObject,
  applyLookChange,
  readDecorLook,
  type DecorLibraryCategory,
  type DecorLook,
  type DecorPicks,
  type ResolvedDecorValues,
} from '@peles/shared';
import { DecorFieldsEditor } from './DecorFieldsEditor';
import { DecorPicksList } from './DecorPicksList';

/** What the control needs of a plan object. */
export interface DressableItem {
  objectType: string;
  isTable: boolean;
  config?: Record<string, unknown> | null;
}

export interface ObjectDecorControlProps {
  /** The objects being dressed — one, or a selection. */
  items: DressableItem[];
  /** "Table 4", "3 tables". */
  forWhat: string;
  /** The plan's look in force (event + plan), what an object without its own shows. */
  planValues: ResolvedDecorValues;
  /** The picks in force on the plan (event's, then the plan's over them). */
  planPicks: DecorPicks;
  /**
   * Changes the objects' own looks: each object's look is passed through the
   * function (so a choice made for several keeps what each had otherwise);
   * null takes every one back to the plan's.
   */
  onChange: (change: ((current: DecorLook) => DecorLook) | null) => void;
  /** "Use on every round table" — offered for one object whose look is its own. */
  onApplyToAllLikeThis?: () => void;
  /** How many objects "like this" there are on the plan. */
  likeThisCount?: number;
}

/** The categories every one of the objects takes. */
export function sharedCategories(items: DressableItem[]): DecorLibraryCategory[] {
  if (items.length === 0) return [];
  const [first, ...rest] = items.map((i) => categoriesForObject(i));
  return first.filter((c) => rest.every((list) => list.includes(c)));
}

/**
 * The decor of one object — or a selection — on the plan: for each kind of
 * piece that dresses it (a table's chairs, linen, flowers, candles, place
 * settings; a bar's front; a sofa's upholstery…), the plan's choice or one of
 * its own from the library, and fine-tuning beneath. The 3D view and the AI
 * render draw each object in its own look.
 */
export function ObjectDecorControl({
  items,
  forWhat,
  planValues,
  planPicks,
  onChange,
  onApplyToAllLikeThis,
  likeThisCount = 0,
}: ObjectDecorControlProps) {
  const [tuning, setTuning] = useState(false);
  const categories = useMemo(() => sharedCategories(items), [items]);
  const looks = items.map((i) => readDecorLook(i.config?.decor ?? null));
  const same = looks.every((l) => JSON.stringify(l) === JSON.stringify(looks[0]));
  const look: DecorLook = same ? (looks[0] ?? {}) : {};
  const own = looks.some((l) => Object.keys(l).length > 0);

  if (categories.length === 0) return null;
  // Only what this choice changed reaches each object — a selection whose
  // looks differ shows none of them, and must not hand that blank back.
  const set = (next: DecorLook) => onChange((current) => applyLookChange(look, next, current));
  const fields = categories.flatMap((c) => DECOR_LIBRARY_CATEGORY_SPECS[c].fields);

  return (
    <section aria-label={`Decor for ${forWhat}`} className="space-y-2" data-testid="object-decor">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <Sparkles className="h-3.5 w-3.5" aria-hidden /> Decor
        </h4>
        {own && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Same as the plan
          </button>
        )}
      </div>
      {!same && (
        <p className="rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
          These differ — a choice here sets it on all of them.
        </p>
      )}
      <DecorPicksList
        compact
        categories={categories}
        look={look}
        onChange={set}
        inheritedPicks={planPicks}
        inheritedLabel="Plan’s"
        forWhat={forWhat}
      />
      <button
        type="button"
        onClick={() => setTuning((t) => !t)}
        aria-expanded={tuning}
        className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
      >
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${tuning ? 'rotate-180' : ''}`} />
        Fine-tune colours and styles
      </button>
      {tuning && (
        <div className="rounded-lg border border-gray-200 p-2">
          <DecorFieldsEditor
            fields={fields}
            look={look}
            onChange={set}
            inherited={planValues as unknown as Record<string, unknown>}
            inheritedLabel="Plan’s"
          />
        </div>
      )}
      {onApplyToAllLikeThis && own && likeThisCount > 1 && (
        <button
          type="button"
          onClick={onApplyToAllLikeThis}
          className="inline-flex w-full items-center justify-center gap-1.5 min-h-11 sm:min-h-8 rounded-md border border-gray-300 px-2 text-xs text-gray-700 hover:bg-gray-50"
        >
          <Copy className="h-3.5 w-3.5" />
          Use on all {likeThisCount} like this
        </button>
      )}
    </section>
  );
}
